import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * AttendanceAutoCheckIn — invisible bridge that runs a GPS-based check-in on
 * mount and whenever the tab becomes visible / focused again. NO continuous
 * background tracking; every attempt is triggered by a user-facing event
 * (mount / visibility / focus). Silent on GPS-permission-denied. Idempotent
 * because the backend enforces "1 check-in per user per day".
 *
 * Mounted from Layout only when the current user has `absensi_enabled=true`.
 */
export default function AttendanceAutoCheckIn() {
  const inFlightRef = useRef(false);
  const lastRunRef = useRef(0);

  const attempt = () => {
    // Throttle to at most one attempt per 20s from the same tab to avoid
    // hammering the backend when the user rapid-switches tabs.
    const now = Date.now();
    if (inFlightRef.current) return;
    if (now - lastRunRef.current < 20000) return;
    if (!navigator.geolocation) return;
    inFlightRef.current = true;
    lastRunRef.current = now;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const body = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy || null,
          };
          // First try to check-in; on success the response is the day's record.
          // If the user has ALREADY checked in earlier today, the backend
          // returns the existing record without duplication.
          try {
            await api.post("/attendance/check-in", body);
          } catch (_) { /* out-of-radius / accuracy — silent */ }
          // Regardless, send a ping so left_office_at / last_inside_at stay
          // up-to-date with the user's current whereabouts.
          try {
            await api.post("/attendance/ping", body);
          } catch (_) { /* silent */ }
        } finally {
          inFlightRef.current = false;
        }
      },
      () => { inFlightRef.current = false; /* permission denied — silent */ },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    // Run once on mount and hook into visibility/focus.
    attempt();
    const onVis = () => { if (document.visibilityState === "visible") attempt(); };
    const onFocus = () => attempt();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
